import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from "react";
import {
  ChatAgentState,
  ChatHistoryItem,
  ChatStep,
  RenderObject,
  SelectedStepParams,
  FileAttachment,
  StepFetcherResponse,
  FeedbackStepParams,
  StepParams,
} from "../types/chat";
import { RcFile } from "antd/lib/upload/interface";
import {
  fetchFeedbackDefinitions,
  submitSupportRequest,
} from "../hooks/useChatHooks";
import { FEEDBACK_EVENTS } from "~/constants/events";
import { FeedbackRateEnum } from "~/constants/feedback";

const initialChatSteps: ChatStep[] = [
  {
    stepId: "support",
    isRoot: true,
    title: "Contact Support",
    inputConfig: {
      enabled: true,
      attachments: true,
      submitHandler: submitSupportRequest,
      successMessage:
        "Your support request has been received. We'll get back to you as soon as possible.",
    },
    renderObjects: [
      {
        responseText:
          "Please describe your issue in detail. If you want import any file, you can click attachment button.",
      },
    ],
  },
  //DON'T REMOVE THIS COMMENTED CODE
  // THIS IS GUIDE FOR FUTURE USE
  // {
  //   stepId: "help",
  //   isRoot: true,
  //   title: "Quick Help",
  //   renderObjects: [
  //     {
  //       responseText: "Please select an option from the list.",
  //     },
  //   ],
  //   steps: [
  //     {
  //       stepId: "helpSystem",
  //       isRoot: false,
  //       title: "System Information",
  //       fetcherFn: fetchSystemInfo,
  //       fetcherParams: {
  //         includePerformance: false,
  //         includeHealth: true,
  //       },
  //       renderObjects: [],
  //     },
  //     {
  //       stepId: "helpDirect",
  //       isRoot: false,
  //       title: "Direct Function Example",
  //       renderObjects: [
  //         {
  //           component: DirectFunctionExample,
  //         },
  //       ],
  //     },
  //     {
  //       stepId: "helpInfoCard",
  //       isRoot: false,
  //       title: "Information Card Example",
  //       renderObjects: [
  //         {
  //           component: ExampleInfoCard,
  //           props: {
  //             title: "System Health Information",
  //             description: "Here's a summary of your system health status:",
  //             items: [
  //               "All subsystems operational",
  //               "Last system check: Today at 14:30",
  //               "Database performance: Excellent",
  //               "API response time: 120ms (normal)",
  //             ],
  //             actionText: "View Detailed Report",
  //           },
  //         },
  //       ],
  //     },
  //     {
  //       stepId: "helpRenderFunction",
  //       isRoot: false,
  //       title: "Render Function Example",
  //       renderObjects: [
  //         {
  //           render: () => (
  //             <div
  //               style={{
  //                 padding: "12px",
  //                 margin: "10px 0",
  //                 background: "#f0f8ff",
  //                 borderRadius: "6px",
  //                 border: "1px solid #d9e8f6",
  //               }}
  //             >
  //               <h4>Custom Render Function</h4>
  //               <p>This content is rendered using a direct render function.</p>
  //               <ul>
  //                 <li>No need to create a separate component</li>
  //                 <li>Great for one-off UI elements</li>
  //                 <li>Can use hooks and state inside the function</li>
  //               </ul>
  //             </div>
  //           ),
  //           responseText: "Here's an example using a render function:",
  //         },
  //       ],
  //     },
  //   ],
  // },
];

const feedbackStep: ChatStep = {
  stepId: "feedback",
  isRoot: false,
  title: "Feedback",
  showBackButton: false,
  fetcherFn: fetchFeedbackDefinitions,
  steps: [],
  onCancel: () => {
    const event = new CustomEvent(FEEDBACK_EVENTS.FEEDBACK_CANCELLED);
    window.dispatchEvent(event);
  },
};

const welcomeMessages = [
  "How can I help you today?",
  "Hi, I'm Mike! Do you have any questions?",
  "How may I assist you?",
  "Do you need support with anything?",
];

const feedbackMessages = {
  initial: {
    [FeedbackRateEnum.LIKE]:
      "Glad you liked it! Did you take action according to my findings?",
    [FeedbackRateEnum.DISLIKE]:
      "Hi, I noticed you didn't like my analysis. Could you let me know why?",
  },
  thanks:
    "Thank you for your feedback. We will use this to improve our responses.",
};

interface ChatContextType {
  state: ChatAgentState;
  isChatVisible: boolean;
  toggleChat: () => void;
  selectStep: (step: ChatStep, params?: StepParams) => void;
  goBack: () => void;
  resetChat: () => void;
  getCurrentOptions: () => Promise<ChatStep[]>;
  getRenderObjects: () => RenderObject[] | undefined;
  getCurrentStep: () => ChatStep | undefined;
  handleSubmit: (text: string, attachments?: RcFile[]) => void;
  startFeedbackFlow: (
    messageId: string,
    conversationId: string,
    feedbackRate?: FeedbackRateEnum,
  ) => void;
  handleCancelCurrentStep: () => Promise<void>;
}

const defaultState: ChatAgentState = {
  history: [],
  selectedSteps: [],
  isTyping: false,
};

const ChatContext = createContext<ChatContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useChatContext = () => {
  const context = useContext(ChatContext);
  if (!context) {
    throw new Error("useChatContext must be used within a ChatProvider");
  }
  return context;
};

interface ChatProviderProps {
  children: ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const [state, setState] = useState<ChatAgentState>(defaultState);
  const [isChatVisible, setIsChatVisible] = useState(false);
  const [chatSteps] = useState<ChatStep[]>(initialChatSteps);
  const [stepDataCache, setStepDataCache] = useState<
    Map<string, StepFetcherResponse>
  >(new Map());

  // Helper function to find a step by its ID
  const findStepById = useCallback(
    (steps: ChatStep[], stepId: string | number): ChatStep | undefined => {
      for (const step of steps) {
        if (step.stepId === stepId) {
          return step;
        }
        if (step.steps && step.steps.length > 0) {
          const found = findStepById(step.steps, stepId);
          if (found) return found;
        }
      }
      return undefined;
    },
    [],
  );

  // Helper function to find a step across all root steps
  const findStepAcrossRoots = useCallback(
    (stepId: string | number): ChatStep | undefined => {
      return [...chatSteps, feedbackStep].reduce(
        (found, rootStep) => {
          if (found) return found;
          return findStepById([rootStep, ...(rootStep.steps || [])], stepId);
        },
        undefined as ChatStep | undefined,
      );
    },
    [chatSteps, findStepById],
  );

  // Helper function to get current step
  const getCurrentStep = useCallback((): ChatStep | undefined => {
    if (state.selectedSteps.length === 0) return undefined;

    const lastSelectedStep =
      state.selectedSteps[state.selectedSteps.length - 1];

    // First try to get from stored step data (for dynamically created steps)
    if (lastSelectedStep.stepData) {
      return lastSelectedStep.stepData;
    }

    // Fallback to finding in the step tree
    return findStepAcrossRoots(lastSelectedStep.stepId);
  }, [state.selectedSteps, findStepAcrossRoots]);

  const loadStepData = useCallback(
    async (
      step: ChatStep,
      params?: StepParams,
    ): Promise<StepFetcherResponse> => {
      try {
        if (step.fetcherFn) {
          const allParams = { ...step.fetcherParams, ...params };

          // Create cache key based on step ID and parameters
          const cacheKey = `${step.stepId}_${JSON.stringify(allParams)}`;

          // Check if we have cached data for this step
          if (stepDataCache.has(cacheKey)) {
            return stepDataCache.get(cacheKey)!;
          }

          const result = await step.fetcherFn(allParams);

          // Cache the result
          setStepDataCache((prev) => new Map(prev).set(cacheKey, result));

          return result;
        }

        return {
          responseText: "No data loader defined for this step.",
        };
      } catch (error) {
        return {
          responseText: `I'm sorry, an error occurred while fetching the data: ${
            error instanceof Error ? error.message : "Unknown error"
          }. Please try again later.`,
        };
      }
    },
    [stepDataCache],
  );

  // Helper function to handle cancellation of current step
  const handleCancelCurrentStep = useCallback(async (): Promise<void> => {
    const currentStep = getCurrentStep();

    if (currentStep?.onCancel) {
      try {
        await currentStep.onCancel();
      } catch (error) {
        throw error as Error;
      }
    }
  }, [getCurrentStep]);

  const toggleChat = useCallback(async () => {
    const nextVisible = !isChatVisible;

    // If closing the chat, handle cancellation of current step
    if (!nextVisible) {
      await handleCancelCurrentStep();
    }

    setIsChatVisible(nextVisible);

    if (!nextVisible) {
      setState((prev) => ({
        ...prev,
        history: [],
        selectedSteps: [],
        isTyping: false,
      }));
      setStepDataCache(new Map()); // Clear cache when closing chat
    }
  }, [handleCancelCurrentStep, isChatVisible]);

  const resetChat = useCallback(() => {
    setState(defaultState);
    setStepDataCache(new Map()); // Clear cache

    const randomIndex = Math.floor(Math.random() * welcomeMessages.length);

    setState({
      ...defaultState,
      history: [
        {
          type: "mike",
          text: welcomeMessages[randomIndex],
          timestamp: Date.now(),
        },
      ],
      isTyping: false,
    });
  }, []);

  const startFeedbackFlow = useCallback(
    async (
      messageId: string,
      conversationId: string,
      feedbackRate?: FeedbackRateEnum,
    ) => {
      setIsChatVisible(true);

      // Show initial message and typing indicator immediately
      setState({
        history: [
          {
            type: "mike",
            text: feedbackMessages.initial[
              feedbackRate || FeedbackRateEnum.DISLIKE
            ],
            timestamp: Date.now(),
          },
        ],
        selectedSteps: [],
        isTyping: true, // Show typing indicator while loading
      });

      // Load feedback step data with parameters
      const feedbackParams: FeedbackStepParams = {
        conversationId,
        conversationResponseId: messageId,
        feedbackRate: feedbackRate,
      };

      try {
        // Load the feedback step data and cache it
        const stepData = await loadStepData(feedbackStep, feedbackParams);

        // Update the feedback step with loaded data
        if (stepData.steps && stepData.steps.length > 0) {
          feedbackStep.steps = stepData.steps;
        }

        // Update state with feedback step and stop typing
        setState((prev) => ({
          ...prev,
          selectedSteps: [
            {
              stepId: "feedback",
              params: feedbackParams,
              stepData: feedbackStep,
            },
          ],
          isTyping: false, // Stop typing indicator after data is loaded
        }));
      } catch (error) {
        setState({
          history: [
            {
              type: "mike",
              text: "I'm sorry, I couldn't load the feedback options at the moment. Please try again later.",
              timestamp: Date.now(),
            },
          ],
          selectedSteps: [],
          isTyping: false,
        });
      }
    },
    [loadStepData],
  );

  useEffect(() => {
    if (isChatVisible && state.history.length === 0) {
      setState((prev) => ({
        ...prev,
        isTyping: true,
      }));

      const timeout = setTimeout(() => {
        const randomIndex = Math.floor(Math.random() * welcomeMessages.length);
        setState((prev) => ({
          ...prev,
          history: [
            ...prev.history,
            {
              type: "mike",
              text: welcomeMessages[randomIndex],
              timestamp: Date.now(),
            },
          ],
          isTyping: false,
        }));
      }, 1500);

      return () => clearTimeout(timeout);
    }
  }, [isChatVisible, state.history.length]);

  useEffect(() => {
    const handleFeedbackDislike = (event: CustomEvent) => {
      const messageId = event.detail?.messageId || "unknown";
      const conversationId = event.detail?.conversationId || "unknown";
      startFeedbackFlow(messageId, conversationId, FeedbackRateEnum.DISLIKE);
    };

    const handleFeedbackLike = (event: CustomEvent) => {
      const messageId = event.detail?.messageId || "unknown";
      const conversationId = event.detail?.conversationId || "unknown";
      startFeedbackFlow(messageId, conversationId, FeedbackRateEnum.LIKE);
    };

    const handleChatClosed = () => {
      setIsChatVisible(false);
    };

    window.addEventListener(
      FEEDBACK_EVENTS.FEEDBACK_DISLIKE,
      handleFeedbackDislike as EventListener,
    );

    window.addEventListener(
      FEEDBACK_EVENTS.FEEDBACK_LIKE,
      handleFeedbackLike as EventListener,
    );

    window.addEventListener(
      FEEDBACK_EVENTS.CHAT_CLOSED,
      handleChatClosed as EventListener,
    );

    return () => {
      window.removeEventListener(
        FEEDBACK_EVENTS.FEEDBACK_DISLIKE,
        handleFeedbackDislike as EventListener,
      );

      window.removeEventListener(
        FEEDBACK_EVENTS.FEEDBACK_LIKE,
        handleFeedbackLike as EventListener,
      );

      window.removeEventListener(
        FEEDBACK_EVENTS.CHAT_CLOSED,
        handleChatClosed as EventListener,
      );
    };
  }, [startFeedbackFlow]);

  const getCurrentOptions = useCallback(async (): Promise<ChatStep[]> => {
    if (state.selectedSteps.length === 0) {
      const rootSteps = chatSteps.filter((step) => step.isRoot);
      return rootSteps;
    }

    const lastSelectedStep =
      state.selectedSteps[state.selectedSteps.length - 1];

    const currentStep = findStepAcrossRoots(lastSelectedStep.stepId);

    if (!currentStep) {
      return [];
    }

    // If step has fetcherFn, check for cached data or step data
    if (currentStep.fetcherFn) {
      const allParams = {
        ...currentStep.fetcherParams,
        ...lastSelectedStep.params,
      };
      const cacheKey = `${currentStep.stepId}_${JSON.stringify(allParams)}`;

      // First check cached data
      if (stepDataCache.has(cacheKey)) {
        const cachedData = stepDataCache.get(cacheKey)!;
        if (cachedData.steps && cachedData.steps.length > 0) {
          return cachedData.steps;
        }
      }

      // Then check if step data is available in selectedSteps (for feedback flow)
      if (lastSelectedStep.stepData && lastSelectedStep.stepData.steps) {
        return lastSelectedStep.stepData.steps;
      }

      return [];
    }

    const steps = currentStep.steps || [];
    return steps;
  }, [chatSteps, state.selectedSteps, findStepAcrossRoots, stepDataCache]);

  const getRenderObjects = useCallback((): RenderObject[] | undefined => {
    if (state.selectedSteps.length === 0) return undefined;

    const lastSelectedStep =
      state.selectedSteps[state.selectedSteps.length - 1];
    const currentStep = findStepAcrossRoots(lastSelectedStep.stepId);

    if (!currentStep) {
      return undefined;
    }

    return currentStep.renderObjects;
  }, [state.selectedSteps, findStepAcrossRoots]);

  // Unified submit handler
  const handleSubmit = useCallback(
    async (text: string, attachments?: RcFile[]) => {
      const currentStep = getCurrentStep();
      const inputConfig = currentStep?.inputConfig;

      if (!inputConfig?.enabled) {
        return;
      }

      // Add user message
      const fileAttachments = attachments?.map((file) => ({
        uid: file.uid,
        name: file.name,
        size: file.size,
        type: file.type,
      })) as FileAttachment[] | undefined;

      const newUserMessage: ChatHistoryItem = {
        type: "user",
        text,
        timestamp: Date.now(),
        attachments: fileAttachments,
      };

      setState((prev) => ({
        ...prev,
        history: [...prev.history, newUserMessage],
        isTyping: true,
      }));

      // Validation
      if (inputConfig.validation) {
        const validationError = inputConfig.validation(text);
        if (validationError) {
          const errorMessage: ChatHistoryItem = {
            type: "mike",
            text: validationError,
            timestamp: Date.now(),
          };

          setState((prev) => ({
            ...prev,
            history: [...prev.history, errorMessage],
            isTyping: false,
          }));
          return;
        }
      }

      // Handle submit
      if (inputConfig.submitHandler) {
        try {
          const result = await inputConfig.submitHandler(text, attachments);

          if (result === true) {
            const successMessage =
              inputConfig.successMessage ||
              "Your request has been processed successfully.";

            const newMikeMessage: ChatHistoryItem = {
              type: "mike",
              text: successMessage,
              timestamp: Date.now(),
            };

            setState((prev) => ({
              ...prev,
              history: [...prev.history, newMikeMessage],
              isTyping: false,
            }));
          } else {
            setState((prev) => ({
              ...prev,
              isTyping: false,
            }));
          }
          return;
        } catch (error) {
          const errorMessage = `I'm sorry, but I encountered an error: ${
            error instanceof Error ? error.message : "Unknown error"
          }`;

          const newMikeMessage: ChatHistoryItem = {
            type: "mike",
            text: errorMessage,
            timestamp: Date.now(),
          };

          setState((prev) => ({
            ...prev,
            history: [...prev.history, newMikeMessage],
            isTyping: false,
          }));
          return;
        }
      }

      // Default response if no handler
      setTimeout(() => {
        const defaultResponse = "I've received your input. Thank you!";

        const newMikeMessage: ChatHistoryItem = {
          type: "mike",
          text: defaultResponse,
          timestamp: Date.now(),
        };

        setState((prev) => ({
          ...prev,
          history: [...prev.history, newMikeMessage],
          isTyping: false,
        }));
      }, 1000);
    },
    [getCurrentStep],
  );

  const selectStep = useCallback(
    async (step: ChatStep, params?: StepParams) => {
      const newUserMessage: ChatHistoryItem = {
        type: "user",
        text: step.title || String(step.stepId),
        timestamp: Date.now(),
      };

      const newSelectedStep: SelectedStepParams = {
        stepId: step.stepId,
        params,
        stepData: step,
      };

      // Only show typing indicator if step has fetcherFn or directSubmitHandler
      const shouldShowTyping = !!(step.fetcherFn || step.directSubmitHandler);

      setState((prev) => ({
        ...prev,
        history: [...prev.history, newUserMessage],
        isTyping: shouldShowTyping,
        selectedSteps: [...prev.selectedSteps, newSelectedStep],
      }));

      // Handle direct submit for immediate actions (like feedback selection)
      if (step.directSubmitHandler) {
        try {
          const result = await step.directSubmitHandler();

          if (result === true) {
            const successMessage =
              "Thank you for your feedback. We will use this to improve our responses.";

            const newMikeMessage: ChatHistoryItem = {
              type: "mike",
              text: successMessage,
              timestamp: Date.now(),
            };

            setState((prev) => ({
              ...prev,
              history: [...prev.history, newMikeMessage],
              isTyping: false,
            }));
            return;
          }
        } catch (error) {
          const errorMessage = `I'm sorry, but I encountered an error: ${
            error instanceof Error ? error.message : "Unknown error"
          }`;

          const newMikeMessage: ChatHistoryItem = {
            type: "mike",
            text: errorMessage,
            timestamp: Date.now(),
          };

          setState((prev) => ({
            ...prev,
            history: [...prev.history, newMikeMessage],
            isTyping: false,
          }));
          return;
        }
      }

      // Load step data if it has a fetcherFn
      if (step.fetcherFn) {
        try {
          // Get current context parameters from selected steps
          const currentStepParams =
            state.selectedSteps.length > 0
              ? state.selectedSteps[state.selectedSteps.length - 1].params
              : {};

          const allParams = {
            ...step.fetcherParams,
            ...currentStepParams,
            ...params,
          };
          const stepData = await loadStepData(step, allParams);

          if (stepData.renderObjects && stepData.renderObjects.length > 0) {
            step.renderObjects = stepData.renderObjects;
          }

          if (stepData.responseText) {
            const newMikeMessage: ChatHistoryItem = {
              type: "mike",
              text: stepData.responseText,
              renderObjects: step.renderObjects,
              timestamp: Date.now(),
            };

            setState((prev) => ({
              ...prev,
              history: [...prev.history, newMikeMessage],
              isTyping: false,
            }));
            return;
          }

          // If we have steps from the fetcher, don't show default message
          if (stepData.steps && stepData.steps.length > 0) {
            setState((prev) => ({
              ...prev,
              isTyping: false,
            }));
            return;
          }
        } catch (error) {
          const errorMessage = `I'm sorry, but I encountered an error: ${
            error instanceof Error ? error.message : "Unknown error"
          }`;

          const newMikeMessage: ChatHistoryItem = {
            type: "mike",
            text: errorMessage,
            timestamp: Date.now(),
          };

          setState((prev) => ({
            ...prev,
            history: [...prev.history, newMikeMessage],
            isTyping: false,
          }));
          return;
        }
      }

      // For steps without fetcherFn or directSubmitHandler, show immediate response
      // Determine response text and render objects
      let responseText: string | undefined;
      const renderObjects: RenderObject[] | undefined = step.renderObjects;

      if (renderObjects && renderObjects.length > 0) {
        const firstResponseObject = renderObjects.find(
          (obj) => obj.responseText,
        );
        if (firstResponseObject) {
          responseText = firstResponseObject.responseText;
        }
      }

      if (!responseText) {
        responseText = `Okay, I will be happy to assist you with ${
          step.title || step.stepId
        }.`;
      }

      const newMikeMessage: ChatHistoryItem = {
        type: "mike",
        text: responseText,
        renderObjects: renderObjects,
        timestamp: Date.now(),
      };

      setState((prev) => ({
        ...prev,
        history: [...prev.history, newMikeMessage],
        isTyping: false,
      }));
    },
    [state.selectedSteps, loadStepData],
  );

  const goBack = useCallback(() => {
    if (state.selectedSteps.length <= 1) {
      resetChat();
      return;
    }

    setState((prev) => ({
      ...prev,
      selectedSteps: prev.selectedSteps.slice(0, -1),
      history: prev.history.slice(0, -2),
    }));
  }, [state.selectedSteps.length, resetChat]);

  return (
    <ChatContext.Provider
      value={{
        state,
        isChatVisible,
        toggleChat,
        selectStep,
        goBack,
        resetChat,
        getCurrentOptions,
        getRenderObjects,
        getCurrentStep,
        handleSubmit,
        startFeedbackFlow,
        handleCancelCurrentStep,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
};

export default ChatContext;
